import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import moment from "moment";
import pulseServices from "app/services/pulse-services";
import { getConsumer } from "app/services/cable";
import {
    CHANNEL_LABELS,
    SOURCE_COLORS,
    SOURCE_LABELS,
    clock,
    describe,
    feedTone,
    timeAgo,
} from "./pulseFormat";

const MAX_FEED = 300;
const TONE_COLORS = { ok: "success.main", warning: "warning.main", error: "error.main" };

export const SourceChip = ({ source, version }) => (
    <Chip
        size="small"
        variant="outlined"
        label={`${SOURCE_LABELS[source] || source || "?"}${version ? ` ${version}` : ""}`}
        sx={{ borderColor: SOURCE_COLORS[source] || "divider", height: 20, fontSize: 11 }}
    />
);

const Stat = ({ label, value, detail }) => (
    <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h4" sx={{ my: 0.25, fontWeight: 600 }}>{value}</Typography>
            {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
        </CardContent>
    </Card>
);

const LiveTab = () => {
    const [day, setDay] = useState(moment().format("YYYY-MM-DD"));
    const [overview, setOverview] = useState(null);
    const [feed, setFeed] = useState([]);
    const [online, setOnline] = useState([]);
    const [connected, setConnected] = useState(false);
    const [filter, setFilter] = useState("all");
    const [companyFilter, setCompanyFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const isToday = day === moment().format("YYYY-MM-DD");
    const seen = useRef(new Set());

    const loadOverview = useCallback(() => {
        pulseServices.overview({ date: day }).then(setOverview).catch(() => {});
    }, [day]);

    const loadOnline = useCallback(() => {
        pulseServices.online().then(setOnline).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            pulseServices.overview({ date: day }).then(setOverview),
            pulseServices.feed({ limit: 150 }).then((items) => {
                seen.current = new Set(items.map((i) => `${i.type}:${i.id}`));
                setFeed(items);
            }),
            pulseServices.online().then(setOnline),
        ]).catch(() => {}).finally(() => setLoading(false));
    }, [day]);

    // Lo que llega en vivo se suma arriba; el resumen y los conectados se refrescan solos.
    useEffect(() => {
        const consumer = getConsumer();
        if (!consumer) return undefined;
        const subscription = consumer.subscriptions.create("PulseChannel", {
            connected: () => setConnected(true),
            disconnected: () => setConnected(false),
            rejected: () => setConnected(false),
            received: (item) => {
                const key = `${item.type}:${item.id}`;
                if (seen.current.has(key)) return;
                seen.current.add(key);
                setFeed((previous) => [item, ...previous].slice(0, MAX_FEED));
            },
        });
        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        if (!isToday) return undefined;
        const a = setInterval(loadOverview, 60000);
        const b = setInterval(loadOnline, 30000);
        return () => {
            clearInterval(a);
            clearInterval(b);
        };
    }, [isToday, loadOverview, loadOnline]);

    const companies = useMemo(() => {
        const map = new Map();
        (overview?.companies || []).forEach((c) => map.set(String(c.company_id), c.name));
        feed.forEach((i) => i.company_id && i.company_name && map.set(String(i.company_id), i.company_name));
        return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    }, [overview, feed]);

    const visibleFeed = useMemo(() => feed.filter((item) => {
        if (companyFilter && String(item.company_id) !== companyFilter) return false;
        if (filter === "errors") return item.type === "error" && item.kind !== "rejected";
        if (filter === "business") return item.type === "activity";
        if (filter === "friction") return item.type === "error" || feedTone(item) !== "ok";
        return true;
    }), [feed, filter, companyFilter]);

    if (loading && !overview) {
        return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress color="warning" /></Stack>;
    }

    const t = overview?.totals || {};

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                    type="date"
                    size="small"
                    label="Día"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <Chip
                    size="small"
                    color={connected ? "success" : "default"}
                    label={connected ? "En vivo" : "Sin conexión en vivo"}
                    variant={connected ? "filled" : "outlined"}
                />
                <Typography variant="caption" color="text.secondary">
                    El resumen se actualiza cada minuto; el feed, al instante.
                </Typography>
            </Stack>

            <Grid container spacing={1.5}>
                <Grid item xs={6} sm={4} md={2}>
                    <Stat label="Usuarios activos" value={t.users ?? 0} detail={`${t.app_users ?? 0} app · ${t.web_users ?? 0} web`} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Stat label="Conectados ahora" value={online.length} detail="últimos 15 min" />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Stat label="Pedidos creados" value={t.orders ?? 0} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Stat label="Viajes del día" value={t.trips ?? 0} detail={`${t.trips_optimized ?? 0} optimizados${t.optimization_errors ? ` · ${t.optimization_errors} con error` : ""}`} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Stat label="Entregas" value={`${t.deliveries_finished ?? 0}/${t.deliveries ?? 0}`} detail={t.deliveries_cancelled ? `${t.deliveries_cancelled} no entregadas` : "cerradas / del día"} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Stat label="Errores" value={(t.server_errors ?? 0) + (t.client_errors ?? 0)} detail={`${t.server_errors ?? 0} servidor · ${t.client_errors ?? 0} cliente · ${t.rejected ?? 0} rechazos`} />
                </Grid>
            </Grid>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Por cliente</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Cliente</TableCell>
                                    <TableCell align="right">Usuarios</TableCell>
                                    <TableCell>Pedidos por canal</TableCell>
                                    <TableCell align="right">Viajes</TableCell>
                                    <TableCell align="right">Optimizados</TableCell>
                                    <TableCell align="right">Entregas</TableCell>
                                    <TableCell align="right">Con firma</TableCell>
                                    <TableCell align="right">Errores</TableCell>
                                    <TableCell>Último uso</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(overview?.companies || []).map((c) => (
                                    <TableRow key={c.company_id} hover>
                                        <TableCell><b>{c.name}</b></TableCell>
                                        <TableCell align="right">
                                            {c.users.total}
                                            <Typography component="span" variant="caption" color="text.secondary"> ({c.users.app} app · {c.users.web} web)</Typography>
                                        </TableCell>
                                        <TableCell>
                                            {Object.entries(c.orders.by_channel).length === 0 ? "-" : Object.entries(c.orders.by_channel).map(([channel, n]) => (
                                                <Chip key={channel} size="small" variant="outlined" sx={{ mr: 0.5, height: 20, fontSize: 11 }} label={`${CHANNEL_LABELS[channel] || channel} ${n}`} />
                                            ))}
                                        </TableCell>
                                        <TableCell align="right">
                                            {c.trips.total}
                                            {c.trips.in_progress > 0 && <Typography component="span" variant="caption" color="text.secondary"> ({c.trips.in_progress} en ruta)</Typography>}
                                        </TableCell>
                                        <TableCell align="right">
                                            {c.trips.optimized}
                                            {c.trips.optimization_errors > 0 && <Typography component="span" variant="caption" color="error.main"> · {c.trips.optimization_errors} error</Typography>}
                                        </TableCell>
                                        <TableCell align="right">
                                            {c.deliveries.finished}/{c.deliveries.total}
                                            {c.deliveries.cancelled > 0 && <Typography component="span" variant="caption" color="error.main"> · {c.deliveries.cancelled} no entregadas</Typography>}
                                        </TableCell>
                                        <TableCell align="right">{c.deliveries.signed}</TableCell>
                                        <TableCell align="right">
                                            {(c.errors.server || 0) + (c.errors.client || 0)}
                                            {c.errors.rejected > 0 && <Typography component="span" variant="caption" color="text.secondary"> +{c.errors.rejected} rech.</Typography>}
                                        </TableCell>
                                        <TableCell>{timeAgo(c.last_seen_at)}</TableCell>
                                    </TableRow>
                                ))}
                                {(overview?.companies || []).length === 0 && (
                                    <TableRow><TableCell colSpan={9}><Typography color="text.secondary">Sin actividad este día.</Typography></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                                <Typography variant="h6" sx={{ mr: 1 }}>Qué está pasando</Typography>
                                <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)}>
                                    <ToggleButton value="all">Todo</ToggleButton>
                                    <ToggleButton value="business">Negocio</ToggleButton>
                                    <ToggleButton value="friction">Fricción</ToggleButton>
                                    <ToggleButton value="errors">Errores</ToggleButton>
                                </ToggleButtonGroup>
                                <TextField select size="small" label="Cliente" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} sx={{ minWidth: 180 }}>
                                    <MenuItem value="">Todos</MenuItem>
                                    {companies.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
                                </TextField>
                            </Stack>
                            <Box sx={{ maxHeight: 640, overflowY: "auto" }}>
                                {visibleFeed.map((item) => (
                                    <Stack key={`${item.type}:${item.id}`} direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 0.75, borderBottom: 1, borderColor: "divider" }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", mt: 0.9, flexShrink: 0, bgcolor: TONE_COLORS[feedTone(item)] }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ width: 60, flexShrink: 0, mt: 0.25 }}>{clock(item.at)}</Typography>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{describe(item)}</Typography>
                                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                                <Typography variant="caption" color="text.secondary">
                                                    {[item.company_name, item.user_name].filter(Boolean).join(" · ") || "Sin usuario"}
                                                </Typography>
                                                <SourceChip source={item.source} version={item.app_version} />
                                            </Stack>
                                        </Box>
                                    </Stack>
                                ))}
                                {visibleFeed.length === 0 && <Typography color="text.secondary" sx={{ py: 2 }}>Nada todavía.</Typography>}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>Conectados ahora ({online.length})</Typography>
                            <Box sx={{ maxHeight: 640, overflowY: "auto" }}>
                                {online.map((u) => (
                                    <Stack key={u.user_id} spacing={0.25} sx={{ py: 0.75, borderBottom: 1, borderColor: "divider" }}>
                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                            <Typography variant="body2" noWrap><b>{u.user_name}</b></Typography>
                                            <Tooltip title={moment(u.at).format("DD/MM HH:mm:ss")}>
                                                <Typography variant="caption" color="text.secondary" noWrap>{timeAgo(u.at)}</Typography>
                                            </Tooltip>
                                        </Stack>
                                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Typography variant="caption" color="text.secondary">{u.company_name}{u.profiles?.length ? ` · ${u.profiles.join(", ")}` : ""}</Typography>
                                            <SourceChip source={u.source} version={u.app_version} />
                                        </Stack>
                                        {(u.screen || u.endpoint) && (
                                            <Typography variant="caption" color="text.secondary" noWrap>{u.screen || u.endpoint}</Typography>
                                        )}
                                    </Stack>
                                ))}
                                {online.length === 0 && <Typography color="text.secondary">Nadie en los últimos 15 minutos.</Typography>}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Stack>
    );
};

export default LiveTab;
